import React, { useState } from 'react';
import { Home } from 'lucide-react';
import sendPreCoatingBatchEmail from './preCoatingBatchEmailService';
import PVSPageLayout from './PVSPageLayout';
import FormSuccessModal from './FormSuccessModal';

const dateToBatchNumber = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}${mm}${yy}`;
};

const PreCoatingBatchQualityForm = ({ onBack }) => {
  const today = new Date().toISOString().split('T')[0];
  const [formData, setFormData] = useState({
    batchNumber: dateToBatchNumber(today),
    inspectorName: 'Adrian Bates',
    date: today,
    numberOfVessels: '',
    surfacePrepGrade: 'Sa2.5',
    surfaceProfileReadings: '',
    ventilationStatus: 'Booth On',
    contaminantsRemoved: 'no',
    previousCoatingRemoved: 'no',
    ambientTemperature: '',
    relativeHumidity: '',
    substrateTemperature: '',
    sprayBoothTempSetPoint: '',
    steelPreheated: 'no',
    primerDftLow: '',
    primerDftHigh: '',
    additionalComments: ''
  });

  const PRIMER_DFT_MIN = 125;

  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const calculateDewPoint = (temp, humidity) => {
    const t = parseFloat(temp);
    const rh = parseFloat(humidity);
    if (isNaN(t) || isNaN(rh) || rh <= 0 || rh > 100) return '';
    const a = 17.27;
    const b = 237.7;
    const alpha = ((a * t) / (b + t)) + Math.log(rh / 100);
    const dewPoint = (b * alpha) / (a - alpha);
    return dewPoint.toFixed(1);
  };

  const calculatedDewPoint = calculateDewPoint(formData.ambientTemperature, formData.relativeHumidity);
  const dewPointSpread = calculatedDewPoint && formData.substrateTemperature 
    ? (parseFloat(formData.substrateTemperature) - parseFloat(calculatedDewPoint)).toFixed(1)
    : '';

  const primerDftLowNum = parseFloat(formData.primerDftLow);
  const primerDftHighNum = parseFloat(formData.primerDftHigh);
  const primerDftAverage = !isNaN(primerDftLowNum) && !isNaN(primerDftHighNum)
    ? ((primerDftLowNum + primerDftHighNum) / 2).toFixed(1)
    : '';
  const primerDftPass = primerDftAverage !== '' && parseFloat(primerDftAverage) >= PRIMER_DFT_MIN
    && !isNaN(primerDftLowNum) && primerDftLowNum >= PRIMER_DFT_MIN;
  const primerDftEvaluated = primerDftAverage !== '';

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({
      ...prev,
      [name]: newValue,
      ...(name === 'date' ? { batchNumber: dateToBatchNumber(value) } : {})
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitMessage('');

    const mandatoryFields = {
      batchNumber: 'Batch Number',
      inspectorName: 'Inspector Name',
      date: 'Date',
      numberOfVessels: 'Number of Vessels',
      surfacePrepGrade: 'Surface Preparation Grade'
    };

    const missingFields = [];
    for (const [field, label] of Object.entries(mandatoryFields)) {
      if (!formData[field]) missingFields.push(label);
    }

    if (missingFields.length > 0) {
      setSubmitMessage(`Please fill in the following mandatory fields: ${missingFields.join(', ')}.`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsSubmitting(true);

    try {
      const dataToSend = {
        ...formData,
        calculatedDewPoint,
        dewPointSpread,
        primerDftAverage,
        primerDftMinimum: PRIMER_DFT_MIN,
        primerDftPass: primerDftEvaluated ? primerDftPass : null
      };
      await sendPreCoatingBatchEmail(dataToSend);
      setFormData({
        batchNumber: dateToBatchNumber(new Date().toISOString().split('T')[0]), inspectorName: 'Adrian Bates', date: new Date().toISOString().split('T')[0],
        numberOfVessels: '', surfacePrepGrade: 'Sa2.5', surfaceProfileReadings: '',
        ventilationStatus: 'Booth On', contaminantsRemoved: 'no', previousCoatingRemoved: 'no',
        ambientTemperature: '', relativeHumidity: '', substrateTemperature: '',
        sprayBoothTempSetPoint: '', steelPreheated: 'no',
        primerDftLow: '', primerDftHigh: '', additionalComments: ''
      });
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitMessage('Error submitting form. Please try again.');
    } finally {
      setIsSubmitting(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <PVSPageLayout className="py-8">
      <div className="max-w-4xl mx-auto">
        <div className="pvs-glass rounded-xl shadow-pvs-glow overflow-hidden border border-pvs-accent/30">
          {/* Header */}
          <div className="border-b border-pvs-border p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-3xl font-bold text-white">Pre Coating Batch Quality Form</h1>
                <p className="text-xs text-pvs-text-dim mt-1">Ref: PVS-PCB-001 | Version: 1.0</p>
              </div>
              <button
                type="button"
                onClick={onBack}
                className="flex items-center justify-center w-12 h-12 flex-shrink-0 rounded-xl pvs-glass pvs-glass-hover transition-all duration-200"
                title="Return to Forms"
              >
                <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
              </button>
            </div>
            <p className="text-pvs-text-dim text-center">Batch Pre-Coating Quality Control Documentation</p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {submitMessage && (
              <div className="p-4 rounded-lg bg-red-900/30 text-red-400 border border-red-500/30">
                <p className="text-sm font-medium">{submitMessage}</p>
              </div>
            )}

            {/* Batch Information Section */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Batch Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Batch Number *</label><input type="text" name="batchNumber" value={formData.batchNumber} onChange={handleInputChange} className="block w-full pvs-input" required /></div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Inspector Name *</label><input type="text" name="inspectorName" value={formData.inspectorName} onChange={handleInputChange} className="block w-full pvs-input" required /></div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Date *</label><input type="date" name="date" value={formData.date} onChange={handleInputChange} className="block w-full pvs-input" required /></div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Number of Vessels in Batch *</label><input type="number" name="numberOfVessels" value={formData.numberOfVessels} onChange={handleInputChange} min="1" className="block w-full pvs-input" required /></div>
              </div>
            </div>

            {/* Pre-Coating Readiness Section */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Pre-Coating Readiness Validation</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Surface Preparation Grade *</label>
                  <select name="surfacePrepGrade" value={formData.surfacePrepGrade} onChange={handleInputChange} className="block w-full pvs-select" required>
                    <option value="">Select grade</option>
                    <option value="Sa1">Sa1 - Light blast cleaning</option>
                    <option value="Sa2">Sa2 - Thorough blast cleaning</option>
                    <option value="Sa2.5">Sa2.5 - Very thorough blast cleaning</option>
                    <option value="Sa3">Sa3 - Blast cleaning to visually clean steel</option>
                  </select>
                </div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Surface Profile Readings (µm)</label><input type="text" name="surfaceProfileReadings" value={formData.surfaceProfileReadings} onChange={handleInputChange} placeholder="e.g., 60, 65, 58" className="block w-full pvs-input" /></div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Ventilation Status</label>
                  <select name="ventilationStatus" value={formData.ventilationStatus} onChange={handleInputChange} className="block w-full pvs-select">
                    <option value="">Select status</option>
                    <option value="Booth On">Booth On</option>
                    <option value="Local Extraction">Local Extraction</option>
                    <option value="None">None</option>
                  </select>
                </div>

                {[
                  { field: 'contaminantsRemoved', label: 'Contaminants Removed (Oil/Grease/Salts)' },
                  { field: 'previousCoatingRemoved', label: 'Previous Coating Removed/Feathered As Per Spec' }
                ].map(({ field, label }) => (
                  <div key={field} className="md:col-span-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-pvs-text-muted">{label}</label>
                      <div className="flex items-center space-x-3">
                        <span className={`text-sm font-medium ${formData[field] === 'no' ? 'text-red-400' : 'text-pvs-text-dim'}`}>No</span>
                        <button type="button" onClick={() => setFormData(prev => ({ ...prev, [field]: formData[field] === 'yes' ? 'no' : 'yes' }))} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-pvs-accent/50 focus:ring-offset-2 ${formData[field] === 'yes' ? 'bg-green-600' : 'bg-red-600'}`}>
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData[field] === 'yes' ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                        <span className={`text-sm font-medium ${formData[field] === 'yes' ? 'text-green-400' : 'text-pvs-text-dim'}`}>Yes</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pre Spray Readings Section */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Pre Spray Readings (Inside Spray Booth)</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Ambient Temperature (°C)</label><input type="number" step="0.1" name="ambientTemperature" value={formData.ambientTemperature} onChange={handleInputChange} className="block w-full pvs-input" /></div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Relative Humidity (%)</label><input type="number" step="0.1" name="relativeHumidity" value={formData.relativeHumidity} onChange={handleInputChange} className="block w-full pvs-input" /></div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Substrate Temperature (°C)</label><input type="number" step="0.1" name="substrateTemperature" value={formData.substrateTemperature} onChange={handleInputChange} className="block w-full pvs-input" /></div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Dew Point (°C) <span className="text-xs text-pvs-text-dim">(Auto-calculated)</span></label><input type="text" value={calculatedDewPoint} readOnly className="block w-full rounded-md border-pvs-border shadow-sm bg-pvs-card-light text-pvs-text-dim px-3 py-2 border cursor-not-allowed" placeholder="Calculated from temp & humidity" /></div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Dew Point Spread (°C) <span className="text-xs text-pvs-text-dim">(Auto-calculated)</span></label><input type="text" value={dewPointSpread} readOnly className="block w-full rounded-md border-pvs-border shadow-sm bg-pvs-card-light text-pvs-text-dim px-3 py-2 border cursor-not-allowed" placeholder="Substrate temp - Dew point" /></div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Spray Booth Temperature Set Point (°C)</label><input type="number" step="0.1" name="sprayBoothTempSetPoint" value={formData.sprayBoothTempSetPoint} onChange={handleInputChange} className="block w-full pvs-input" /></div>

                <div className="md:col-span-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-pvs-text-muted">Is Steel Pre-heated Prior to Paint Application?</label>
                    <div className="flex items-center space-x-3">
                      <span className={`text-sm font-medium ${formData.steelPreheated === 'no' ? 'text-red-400' : 'text-pvs-text-dim'}`}>No</span>
                      <button type="button" onClick={() => setFormData(prev => ({ ...prev, steelPreheated: formData.steelPreheated === 'yes' ? 'no' : 'yes' }))} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-pvs-accent/50 focus:ring-offset-2 ${formData.steelPreheated === 'yes' ? 'bg-green-600' : 'bg-red-600'}`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.steelPreheated === 'yes' ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                      <span className={`text-sm font-medium ${formData.steelPreheated === 'yes' ? 'text-green-400' : 'text-pvs-text-dim'}`}>Yes</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Primer Coat DFT Final Check */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-2">Primer Coat DFT Final Check</h2>
              <p className="text-xs text-pvs-text-dim mb-4">Minimum requirement: {PRIMER_DFT_MIN} µm</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Low Reading (µm)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    name="primerDftLow"
                    value={formData.primerDftLow}
                    onChange={handleInputChange}
                    placeholder={`min ${PRIMER_DFT_MIN}`}
                    className="block w-full pvs-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">High Reading (µm)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    name="primerDftHigh"
                    value={formData.primerDftHigh}
                    onChange={handleInputChange}
                    placeholder={`min ${PRIMER_DFT_MIN}`}
                    className="block w-full pvs-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">
                    Average (µm) <span className="text-xs text-pvs-text-dim">(Auto-calculated)</span>
                  </label>
                  <input
                    type="text"
                    value={primerDftAverage}
                    readOnly
                    className="block w-full rounded-md border-pvs-border shadow-sm bg-pvs-card-light text-pvs-text-dim px-3 py-2 border cursor-not-allowed"
                    placeholder="(Low + High) / 2"
                  />
                </div>
              </div>
              {primerDftEvaluated && (
                <div
                  className={`mt-4 p-3 rounded-lg border text-sm font-medium ${
                    primerDftPass
                      ? 'bg-green-900/30 border-green-500/40 text-green-400'
                      : 'bg-red-900/30 border-red-500/40 text-red-400'
                  }`}
                >
                  {primerDftPass
                    ? `PASS — All readings meet the ${PRIMER_DFT_MIN} µm minimum DFT requirement.`
                    : `FAIL — One or more readings are below the ${PRIMER_DFT_MIN} µm minimum DFT requirement.`}
                </div>
              )}
            </div>

            {/* Additional Comments */}
            <div className="pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Additional Comments</h2>
              <textarea name="additionalComments" value={formData.additionalComments} onChange={handleInputChange} rows={4} className="block w-full pvs-input" placeholder="Enter any additional comments or observations..." />
            </div>

            {/* Submit Button */}
            <div className="flex justify-between pt-4 border-t border-pvs-border">
              <button type="button" onClick={onBack} className="flex items-center justify-center gap-2 px-4 py-2 border border-pvs-accent/40 rounded-md shadow-sm text-sm font-medium text-pvs-accent bg-transparent hover:bg-pvs-accent/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pvs-accent/50">
                <Home className="h-4 w-4" />
                Back to Forms
              </button>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-8 py-3 bg-pvs-accent text-pvs-bg font-semibold rounded-lg shadow-lg hover:bg-pvs-accent-dim transition-all duration-200 ${
                  isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Form'}
              </button>
            </div>
          </form>
        </div>
      </div>
      <FormSuccessModal isOpen={showSuccessModal} formName="Pre Coating Batch Quality" onClose={onBack} />
    </PVSPageLayout>
  );
};

export default PreCoatingBatchQualityForm;
